import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { DragToCloseOptions, ModalCloseSource, UseDragToCloseReturn } from './modal-v2.types';

const VELOCITY_CLOSE_THRESHOLD = 0.5; // px/ms — fast flick closes regardless of distance
const MIN_DISTANCE_FOR_VELOCITY = 30; // px — minimum drag before velocity check kicks in
// Must exceed the Headless UI enter transition (450ms in modal CSS classes) so inline
// style overrides are cleared only after the transition finishes.
const ENTER_TRANSITION_CLEANUP_DELAY = 500;

/**
 * Hook to handle drag-to-close functionality for modals on mobile devices.
 *
 * Uses refs and direct DOM manipulation for 60fps performance — React only
 * re-renders when the closing animation starts, not during the drag.
 */
export const useDragToClose = ({
  isOpen,
  onClose,
  enabled = true,
  threshold = 100,
  closingAnimationDuration = 350,
}: DragToCloseOptions): UseDragToCloseReturn => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const isClosingRef = useRef(false);

  // Refs for tracking drag state without re-renders
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const startTimeRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeInitiatedRef = useRef(false);
  const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset when modal opens or when a close is cancelled (isClosing reverts while isOpen stays true)
  useEffect(() => {
    if (isOpen && !isClosing) {
      const isRecovery = closeInitiatedRef.current;
      closeInitiatedRef.current = false;

      isDraggingRef.current = false;
      startYRef.current = 0;
      currentYRef.current = 0;

      if (panelRef.current) {
        // Restore pointer-events on recovery or fresh open
        const dialogEl = panelRef.current.closest('[role="dialog"]');
        if (dialogEl instanceof HTMLElement) {
          dialogEl.style.pointerEvents = '';
        }

        if (isRecovery) {
          // Close was intercepted (e.g. confirmation modal blocked it).
          // Override Headless UI's enterFrom class so the panel snaps back
          // instantly instead of replaying the slide-up enter animation.
          panelRef.current.style.transform = 'translateY(0)';
          panelRef.current.style.transition = 'none';

          // Clear inline overrides after Headless UI enter transition completes
          cleanupTimeoutRef.current = setTimeout(() => {
            if (panelRef.current) {
              panelRef.current.style.transform = '';
              panelRef.current.style.transition = '';
            }
            cleanupTimeoutRef.current = null;
          }, ENTER_TRANSITION_CLEANUP_DELAY);
        } else {
          panelRef.current.style.transform = '';
          panelRef.current.style.transition = '';
        }
      }
    } else if (!isOpen) {
      closeInitiatedRef.current = false;
    }
  }, [isOpen, isClosing]);

  // Clean up rAF and timeouts on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (closeTimeoutRef.current !== null) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (cleanupTimeoutRef.current !== null) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, []);

  // Two capture-phase document listeners registered in useLayoutEffect
  // (which fires before useEffect) so they're added to `document` before
  // Headless UI's Dialog registers its own via useEffect.
  //
  // 1. touchmove { passive: false } — React 18 registers onTouchMove as a
  //    passive listener, so e.preventDefault() inside the React handler is
  //    silently ignored. Without a working preventDefault the browser treats
  //    the drag as a scroll gesture; after a fast flick it enters fling
  //    deceleration and the user's next tap is consumed stopping the fling
  //    instead of producing a click. Registering our own non-passive handler
  //    directly on the DOM fixes this.
  //
  // 2. touchend — During the closing animation HUI's useOutsideClick
  //    capture-phase touchend handler calls preventDefault(), killing the
  //    browser's synthesized click. stopImmediatePropagation() prevents
  //    HUI's handler from running while still allowing click synthesis.
  useLayoutEffect(() => {
    const handleTouchMoveCapture = (e: TouchEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault();
      }
    };

    const handleTouchEndCapture = (e: TouchEvent) => {
      if (isClosingRef.current) {
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener('touchmove', handleTouchMoveCapture, { passive: false, capture: true });
    document.addEventListener('touchend', handleTouchEndCapture, true);
    return () => {
      document.removeEventListener('touchmove', handleTouchMoveCapture, true);
      document.removeEventListener('touchend', handleTouchEndCapture, true);
    };
  }, []);

  const applyTransform = useCallback((translateY: number) => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => {
      if (panelRef.current) {
        panelRef.current.style.transform = `translateY(${translateY}px)`;
        panelRef.current.style.transition = 'none';
      }
      rafIdRef.current = null;
    });
  }, []);

  const snapBack = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (panelRef.current) {
      panelRef.current.style.transition = `transform ${closingAnimationDuration}ms cubic-bezier(0.32, 0.72, 0, 1)`;
      panelRef.current.style.transform = 'translateY(0px)';
    }
  }, [closingAnimationDuration]);

  const initiateClose = useCallback(
    (source?: ModalCloseSource) => {
      closeInitiatedRef.current = true;
      isClosingRef.current = true;
      // Animate panel off-screen
      if (panelRef.current) {
        panelRef.current.style.transition = `transform ${closingAnimationDuration}ms cubic-bezier(0.32, 0.72, 0, 1)`;
        panelRef.current.style.transform = 'translateY(100%)';

        // Make the Dialog transparent to pointer events so taps pass through
        // to elements behind it during the closing animation. Without this,
        // the fixed backdrop overlay intercepts taps even though the panel
        // is sliding away. Only for drag-close — backdrop taps should NOT
        // punch through, or the user's tap lands on the page underneath.
        if (source === 'drag') {
          const dialogEl = panelRef.current.closest('[role="dialog"]');
          if (dialogEl instanceof HTMLElement) {
            dialogEl.style.pointerEvents = 'none';
          }
        }
      }
      setIsClosing(true);
      closeTimeoutRef.current = setTimeout(() => {
        onClose(source);
        isClosingRef.current = false;
        setIsClosing(false);
        closeTimeoutRef.current = null;
      }, closingAnimationDuration);
    },
    [onClose, closingAnimationDuration]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      startYRef.current = touch.clientY;
      currentYRef.current = 0;
      startTimeRef.current = Date.now();
      isDraggingRef.current = true;
    },
    [enabled]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || !isDraggingRef.current) return;
      // Note: preventDefault() to block scroll is handled by the non-passive
      // capture-phase listener registered in useLayoutEffect above. React 18
      // registers onTouchMove as passive, so calling e.preventDefault() here
      // would be silently ignored by the browser.
      const touch = e.touches[0];
      const diffY = touch.clientY - startYRef.current;

      // Only allow dragging down (positive Y direction)
      if (diffY > 0) {
        currentYRef.current = diffY;
        applyTransform(diffY);
      }
    },
    [enabled, applyTransform]
  );

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !isDraggingRef.current) return;
    isDraggingRef.current = false;

    const distance = currentYRef.current;
    const elapsed = Date.now() - startTimeRef.current;
    const velocity = elapsed > 0 ? distance / elapsed : 0;

    // Close if dragged beyond threshold OR fast flick detected (with minimum distance)
    if (distance > threshold || (distance > MIN_DISTANCE_FOR_VELOCITY && velocity > VELOCITY_CLOSE_THRESHOLD)) {
      initiateClose('drag');
    } else {
      snapBack();
    }
  }, [enabled, threshold, initiateClose, snapBack]);

  const handleTouchCancel = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    snapBack();
  }, [snapBack]);

  return {
    panelRef,
    isClosing,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    initiateClose,
  };
};
